import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { PlacementSummary } from '../services/api'

dayjs.extend(utc)
dayjs.extend(timezone)

const PACIFIC_TIMEZONE = 'America/Los_Angeles'

export type ScheduleStatus = { label: string; variant: 'success' | 'primary' | 'neutral' }

/** Where a start/end window sits relative to now, in the vocabulary the
 * placements list already uses. Shared with hero-banner slides, which carry the
 * same optional half-open window inside their widget metadata — an author who
 * has scheduled a placement reads a scheduled slide without relearning it. */
export const getScheduleStatus = (
  startsAt: DateInput,
  endsAt: DateInput,
): ScheduleStatus => {
  const now = dayjs().tz(PACIFIC_TIMEZONE)
  if (startsAt && dayjs(startsAt).tz(PACIFIC_TIMEZONE).isAfter(now)) return { label: 'Scheduled', variant: 'primary' }
  if (endsAt && dayjs(endsAt).tz(PACIFIC_TIMEZONE).isBefore(now)) return { label: 'Expired', variant: 'neutral' }
  return { label: 'Live', variant: 'success' }
}

export const getPlacementStatus = (p: PlacementSummary): ScheduleStatus => {
  if (!p.is_active) return { label: 'Inactive', variant: 'neutral' }
  return getScheduleStatus(p.starts_at, p.ends_at)
}

/** What the schedule helpers accept: a Date (a placement's parsed row) or an
 * ISO-8601 string (a hero slide's stored metadata), or neither. */
export type DateInput = Date | string | null | undefined

export const formatCompactDate = (date: Date | string): string => {
  const d = typeof date === 'string' ? new Date(date) : date
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: PACIFIC_TIMEZONE })
    + ' ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: PACIFIC_TIMEZONE })
}

/** A window as one line: "Always", a range, or the one bound that is set. */
export const formatScheduleRange = (startsAt: DateInput, endsAt: DateInput): string => {
  if (!startsAt && !endsAt) return 'Always'
  if (startsAt && endsAt) return `${formatCompactDate(startsAt)} – ${formatCompactDate(endsAt)}`
  if (startsAt) return `From ${formatCompactDate(startsAt)}`
  return `Until ${formatCompactDate(endsAt!)}`
}

export const getScheduleDisplay = (p: PlacementSummary): string =>
  formatScheduleRange(p.starts_at, p.ends_at)
